// Frontend/src/components/leave/LeaveRequestForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeave } from '../../context/LeaveContext';
import { leaveService } from '../../api/services';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';
import { Calendar, Clock, FileText, AlertCircle, CheckCircle2, Sun, Moon } from 'lucide-react';
import DateInput from '../common/DateInput';

const LeaveRequestForm = ({ employeeId = null, isManagerRequest = false, hideCard = false }) => {
  const navigate = useNavigate();
  const { leaveTypes, leaveBalance, refreshLeaveBalance, fetchLeaveBalance, fetchLeaveTypes } = useLeave();
  const userProfile = useEmployeeProfile();

  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
    employeeId: employeeId,
  });

  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);

  // Selected dates with Full/Half day selection
  const [selectedDates, setSelectedDates] = useState([]);
  // Work calendar data to identify weekends
  const [workCalendarData, setWorkCalendarData] = useState([]);

  // Fetch leave types when employee profile is available
  useEffect(() => {
    const loadLeaveTypes = async () => {
      const personnelNumber = isManagerRequest ? employeeId : userProfile?.employeeNo;
      if (!personnelNumber) {
        console.log('Waiting for personnel number to fetch leave types...');
        return;
      }

      setLoadingTypes(true);
      try {
        await fetchLeaveTypes(personnelNumber);
      } catch (err) {
        console.error('Failed to load leave types:', err);
      } finally {
        setLoadingTypes(false);
      }
    };
    loadLeaveTypes();
  }, [fetchLeaveTypes, userProfile?.employeeNo, isManagerRequest, employeeId]);

  // Fetch leave balance so the available balance shows correctly on the form
  useEffect(() => {
    const personnelNumber = isManagerRequest ? employeeId : userProfile?.employeeNo;
    if (!personnelNumber) return;
    fetchLeaveBalance(personnelNumber).catch(err => {
      console.error('Failed to load leave balance:', err);
    });
  }, [fetchLeaveBalance, userProfile?.employeeNo, isManagerRequest, employeeId]);

  // Auto-select the first leave type when leave types are loaded (if only one)
  useEffect(() => {
    if (leaveTypes.length === 1 && !formData.leaveTypeId) {
      const defaultType = leaveTypes[0];
      setFormData(prev => ({
        ...prev,
        leaveTypeId: defaultType.id,
      }));
      setSelectedLeaveType(defaultType);
      console.log('Auto-selected leave type:', defaultType.id);
    }
  }, [leaveTypes, formData.leaveTypeId]);

  useEffect(() => {
    if (formData.leaveTypeId) {
      const type = leaveTypes.find(lt => lt.id === formData.leaveTypeId);
      setSelectedLeaveType(type);
    }
  }, [formData.leaveTypeId, leaveTypes]);

  // Update amounts when leave type unit changes (e.g., switching between Hours and Days)
  useEffect(() => {
    if (selectedLeaveType && selectedDates.length > 0) {
      const isHours = selectedLeaveType.leaveAmountUnit === 'Hours';
      const defaultAmount = isHours ? 8 : 1;

      // Only update if the current amounts don't match the expected defaults
      const needsUpdate = selectedDates.some(day => {
        if (isHours) {
          return day.amount === 1 || day.amount === 0.5; // Was set for Days
        } else {
          return day.amount !== 1 && day.amount !== 0.5; // Was set for Hours
        }
      });

      if (needsUpdate) {
        setSelectedDates(prev => prev.map(day => ({
          ...day,
          dayType: isHours ? 'full' : day.dayType,
          amount: defaultAmount,
        })));
      }
    }
  }, [selectedLeaveType?.leaveAmountUnit]);

  // Fetch work calendar when date range changes
  useEffect(() => {
    const fetchWorkCalendar = async () => {
      if (!formData.startDate || !formData.endDate || !userProfile?.employeeNo) {
        return;
      }

      setLoadingCalendar(true);
      try {
        const start = formData.startDate;
        const end = formData.endDate;

        const calendarParams = {
          $filter: `CalendarDate ge ${start}T00:00:00Z and CalendarDate le ${end}T23:59:59Z`,
          $orderby: 'CalendarDate asc',
        };

        const response = await leaveService.getWorkCalendarDays(userProfile.employeeNo, calendarParams);
        const calendarData = response.data?.value || [];
        setWorkCalendarData(calendarData);

        // Generate selected dates excluding weekends/closed days
        generateSelectedDates(formData.startDate, formData.endDate, calendarData);
      } catch (err) {
        console.error('Failed to fetch work calendar:', err);
        // If calendar fetch fails, still generate dates but include all days
        generateSelectedDates(formData.startDate, formData.endDate, []);
      } finally {
        setLoadingCalendar(false);
      }
    };

    fetchWorkCalendar();
  }, [formData.startDate, formData.endDate, userProfile?.employeeNo]);

  // Helper function to parse DD/MM/YYYY date format
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    // Check if date is in DD/MM/YYYY format
    const ddmmyyyyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      return new Date(year, month - 1, day);
    }
    // Otherwise try standard Date parsing (YYYY-MM-DD, ISO, etc.)
    return new Date(dateStr);
  };

  // Helper function to format date as YYYY-MM-DD in local timezone (no UTC conversion)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate list of dates with Full/Half day selection
  const generateSelectedDates = (startDate, endDate, calendarData) => {
    if (!startDate || !endDate) {
      setSelectedDates([]);
      return;
    }

    const start = parseDate(startDate);
    const end = parseDate(endDate);
    const dates = [];

    // Create a map of calendar data for quick lookup
    const calendarMap = {};
    calendarData.forEach(day => {
      const dateStr = day.CalendarDate.split('T')[0];
      calendarMap[dateStr] = day.Status; // Open, Closed, Holiday
    });

    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
      const dateStr = formatDateLocal(dt);
      const status = calendarMap[dateStr];

      // Exclude weekends (Closed) and Holidays
      // If no calendar data, use JavaScript day check (0 = Sunday, 6 = Saturday)
      const dayOfWeek = dt.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      // Skip if status is Closed or Holiday, or if it's a weekend (when no calendar data)
      if (status === 'Closed' || status === 'Holiday') {
        continue;
      }
      if (!status && isWeekend) {
        continue;
      }

      // Default amount: 8 hours for Hours unit, 1 day for Days unit
      const defaultAmount = selectedLeaveType?.leaveAmountUnit === 'Hours' ? 8 : 1;
      dates.push({
        date: dateStr,
        dayType: 'full', // 'full' or 'half'
        amount: defaultAmount,
        dayName: dt.toLocaleDateString('en-GB', { weekday: 'long' }),
        formattedDate: dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      });
    }

    setSelectedDates(dates);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  // Handle day type change (Full/Half) - for Days unit
  const handleDayTypeChange = (index, dayType) => {
    setSelectedDates(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        dayType: dayType,
        amount: dayType === 'full' ? 1 : 0.5,
      };
      return updated;
    });
  };

  // Round to 1 decimal place to avoid floating-point artifacts (e.g. 8.1 + 8.6 -> 16.700000000000003)
  const roundToOneDecimal = (value) => Math.round(value * 10) / 10;

  // Clamp an hours value into the valid 0-24 range, rounded to 1 decimal place
  const clampHours = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return roundToOneDecimal(Math.min(24, Math.max(0, num)));
  };

  // Handle hours input change - for Hours unit
  const handleHoursChange = (index, hours) => {
    const numHours = clampHours(hours);
    setSelectedDates(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        amount: numHours,
      };
      return updated;
    });
  };

  // Set all days to Full or Half - for Days unit
  const setAllDaysType = (dayType) => {
    setSelectedDates(prev => prev.map(day => ({
      ...day,
      dayType: dayType,
      amount: dayType === 'full' ? 1 : 0.5,
    })));
  };

  // Set all days to same hours - for Hours unit
  const setAllHours = (hours) => {
    const numHours = clampHours(hours);
    setSelectedDates(prev => prev.map(day => ({
      ...day,
      amount: numHours,
    })));
  };

  // Check if selected leave type uses Hours
  const isHoursUnit = selectedLeaveType?.leaveAmountUnit === 'Hours';

  const isDirty = formData.startDate !== '' || formData.endDate !== '' || formData.reason !== '' || selectedDates.length > 0;

  const handleCancel = () => {
    setFormData({ leaveTypeId: '', startDate: '', endDate: '', reason: '', employeeId });
    setSelectedDates([]);
    setSelectedLeaveType(null);
    setError(null);
  };

  const validateForm = () => {
    if (!formData.leaveTypeId) {
      setError('Please select a leave type');
      return false;
    }
    if (!formData.startDate) {
      setError('Please select a start date');
      return false;
    }
    if (!formData.endDate) {
      setError('Please select an end date');
      return false;
    }
    if (parseDate(formData.startDate) > parseDate(formData.endDate)) {
      setError('End date must be after start date');
      return false;
    }
    if (selectedDates.length === 0) {
      setError('No working days in the selected date range');
      return false;
    }
    return true;
  };

  // Calculate total days
  const calculateTotalDays = () => {
    return roundToOneDecimal(selectedDates.reduce((total, day) => total + day.amount, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    if (!userProfile.employeeNo && !isManagerRequest) {
      setError('Unable to get employee number. Please try refreshing the page.');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        personnelNumber: isManagerRequest ? employeeId : userProfile.employeeNo,
        leaveTypeId: formData.leaveTypeId,
        reason: formData.reason,
        halfDayDefinition: 'None',
        dataAreaId: selectedLeaveType?.dataAreaId || 'usmf',
        // Pass individual dates with their amounts
        leaveDays: selectedDates.map(day => ({
          date: day.date,
          amount: day.amount,
        })),
      };

      console.log('Submitting leave request:', requestData);

      await leaveService.createLeaveRequestWithDays(requestData);

      setSuccess(true);
      console.log('Leave request submitted successfully');

      alert('Leave request submitted successfully!');

      await refreshLeaveBalance(requestData.personnelNumber);

      navigate('/portal/leave/management', {
        state: { activeTab: 'history' }
      });
    } catch (err) {
      console.error('Leave request submission failed:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableBalance = () => {
    if (!selectedLeaveType || !leaveBalance) return null;
    // Leave balance records come from Dynamics with PascalCase fields
    const balance = leaveBalance.find(b => b.LeaveTypeId === selectedLeaveType.id);
    if (!balance || balance.BalanceAvailable == null) return null;
    return balance.BalanceAvailable;
  };

  const availableBalance = getAvailableBalance();
  const totalRequestedDays = calculateTotalDays();

  const content = (
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FileText className="w-6 h-6 text-indigo-600" />
        {isManagerRequest ? 'Request Leave on Behalf' : 'Request Leave'}
      </h2>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <p className="text-green-800">Leave request submitted successfully!</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Leave Type Dropdown */}
        <div>
          <label htmlFor="leaveTypeId" className="block text-sm font-medium text-gray-700 mb-2">
            Leave Type <span className="text-red-500">*</span>
          </label>
          {loadingTypes ? (
            <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="text-gray-500">Loading leave types...</span>
            </div>
          ) : leaveTypes.length === 0 ? (
            <div className="w-full px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-red-600">No leave types available. Please contact administrator.</span>
            </div>
          ) : (
            <select
              id="leaveTypeId"
              name="leaveTypeId"
              value={formData.leaveTypeId}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              required
            >
              <option value="">Select Leave Type</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.id} - {type.description || type.name}
                </option>
              ))}
            </select>
          )}
          {selectedLeaveType && availableBalance !== null && (
            <p className="mt-2 text-sm text-gray-600">
              Available balance: <span className="font-semibold">{availableBalance} {isHoursUnit ? 'hours' : 'days'}</span>
            </p>
          )}
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date <span className="text-red-500">*</span>
            </label>
            <DateInput
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date <span className="text-red-500">*</span>
            </label>
            <DateInput
              id="endDate"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              min={formData.startDate}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
        </div>

        {/* Selected Dates List */}
        {loadingCalendar ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500"></div>
            <span className="ml-3 text-gray-600">Loading work calendar...</span>
          </div>
        ) : selectedDates.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Selected Days ({selectedDates.length} working days) - <span className="text-indigo-600 font-semibold">{isHoursUnit ? 'Hours' : 'Days'}</span>
              </label>
              {!isHoursUnit ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAllDaysType('full')}
                    className="text-xs px-3 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                  >
                    All Full Day
                  </button>
                  <button
                    type="button"
                    onClick={() => setAllDaysType('half')}
                    className="text-xs px-3 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
                  >
                    All Half Day
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Set all to:</span>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.1"
                    placeholder="Hours"
                    className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onChange={(e) => setAllHours(e.target.value)}
                  />
                  <span className="text-xs text-gray-500">hours</span>
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                    {!isHoursUnit && (
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                    )}
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      {isHoursUnit ? 'Hours' : 'Days'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedDates.map((day, index) => (
                    <tr key={day.date} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{day.formattedDate}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{day.dayName}</td>
                      {!isHoursUnit ? (
                        <>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleDayTypeChange(index, 'full')}
                                className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                  day.dayType === 'full'
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <Sun className="w-3 h-3" />
                                Full
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDayTypeChange(index, 'half')}
                                className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                  day.dayType === 'half'
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <Moon className="w-3 h-3" />
                                Half
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-right text-gray-900">
                            {day.amount}
                          </td>
                        </>
                      ) : (
                        <td className="px-4 py-3 text-right">
                          <input
                            type="number"
                            min="0"
                            max="24"
                            step="0.1"
                            value={day.amount}
                            onChange={(e) => handleHoursChange(index, e.target.value)}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-right"
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary */}
        {selectedDates.length > 0 && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-semibold">
                Total {isHoursUnit ? 'Hours' : 'Days'}: {totalRequestedDays}
              </span>
              {availableBalance !== null && (
                <span className="ml-2">
                  (Remaining after request: {roundToOneDecimal(availableBalance - totalRequestedDays)} {isHoursUnit ? 'hours' : 'days'})
                </span>
              )}
            </p>
          </div>
        )}

        {/* Reason */}
        <div>
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
            Reason
          </label>
          <textarea
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Please provide a reason for your leave request..."
          />
        </div>

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading || leaveTypes.length === 0 || loadingTypes || selectedDates.length === 0}
            className="flex-1 bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
          {isDirty && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </>
  );

  if (hideCard) {
    return content;
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
      {content}
    </div>
  );
};

export default LeaveRequestForm;
